import React, { useEffect, useRef, useState } from 'react';
import './Approach.css';
const steps = [
  { title: 'Diagnose', description: 'Find the bottleneck and agree what success looks like.', detail: 'Priorities, a baseline and success measures.', output: 'A clear starting point.' },
  { title: 'Redesign the process', description: 'Simplify the work before choosing tools.', detail: 'Clear workflows, responsibilities and handoffs.', output: 'A better way to work.' },
  { title: 'Choose the right technology', description: 'Assess existing tools. Add only what the business needs.', detail: 'Solution architecture and a phased plan.', output: 'Technology with a purpose.' },
  { title: 'Implement & embed', description: 'Connect and configure. Test against agreed requirements.', detail: 'QA, training and adoption, governance, documentation and handover.', output: 'A team ready to own it.' }
];
const flowNotes = [
  'The enquiry is here. The next owner is not clear.',
  'Agree who responds, what happens next and when.',
  'Keep the customer record. Add a follow-up reminder.',
  'Tested with the team. Ready for everyday use.'
];
// The enquiry, owner, response and outcome persist through all four states.
// Geometry changes first; technology supports the redesigned route; ownership closes it.
const MethodFlow = ({ stage }) => {
  const points = stage === 0 ? [[45,110],[177,75],[294,130],[400,100]] : [[45,110],[160,110],[280,110],[400,110]];
  const labels = stage === 0 ? ['Enquiry', 'Owner?', 'Delayed', 'Unknown'] : ['Enquiry', 'Owner', 'Follow-up', 'Outcome'];
  return (
    <div className={`method-flow stage-${stage}`}>
      <span className="method-example">One enquiry &middot; an illustrative example</span>
      <svg className="method-map" viewBox="0 0 450 300" role="img" aria-label={[
        'An enquiry stalls at an unclear owner. The response is delayed and the outcome is unknown.',
        'The route is simplified: enquiry, named owner, agreed follow-up, visible outcome.',
        'The same route is supported by the existing customer record and one added follow-up reminder.',
        'The route is tested and working. The team owns the flow and reviews outcomes to improve it.'
      ][stage]}>
        <g className="method-contours"><ellipse cx="222" cy="110" rx="193" ry="92"/><ellipse cx="222" cy="110" rx="158" ry="67"/></g>
        {stage === 0 ? <g className="method-unresolved">
          <path d="M45 110C78 110 86 75 124 75h22M207 75c40 0 23 55 56 55M324 130q30 0 47-25"/>
          <path d="M89 96c-12 47 74 48 51 7M292 130v50h-57"/>
        </g> : <path className="method-connected" d="M45 110H400"/>}
        <g className="method-focus-lens" style={{ transform: `translate(${stage === 0 ? 177 : stage === 1 ? 160 : stage === 2 ? 280 : 400}px, ${stage === 0 ? 75 : 110}px)` }}>
          <circle r="36"/><circle className="method-lens-inner" r="27"/>
        </g>
        {stage === 0 && <g className="method-annotation"><path d="M177 35V18h75"/><text x="259" y="23">Handoff missing</text></g>}
        {stage === 1 && <g className="method-annotation"><path d="M160 72V40h120v32"/><text x="220" y="28" textAnchor="middle">Ownership + next action</text></g>}
        {stage >= 2 && <g className="method-support" key="support">
          <path d="M160 125v73h-30m150-73v73h32"/>
          <circle cx="130" cy="198" r="5"/><circle cx="312" cy="198" r="5"/>
          <text x="130" y="229" textAnchor="middle">Customer record</text><text className="method-svg-note" x="130" y="250" textAnchor="middle">KEEP & CONNECT</text>
          <text x="322" y="229" textAnchor="middle">Reminder</text><text className="method-svg-note" x="322" y="250" textAnchor="middle">ADD WHERE NEEDED</text>
        </g>}
        {stage === 3 && <g className="method-ownership">
          <path d="M400 90V43Q400 28 385 28H60Q45 28 45 43v38m-6-6 6 6 6-6"/>
          <rect x="118" y="15" width="210" height="26"/><text x="223" y="34" textAnchor="middle">Team owns & reviews</text>
        </g>}
        {points.map(([x,y],i)=><g key={i} className={`method-business-node node-${i}`} style={{ transform: `translate(${x}px, ${y}px)` }}>
          <circle r="13"/>{stage === 3 ? <path d="m-5 0 3 3 7-7"/> : <circle className="method-point" r="3"/>}
          <text y="43" textAnchor="middle">{labels[i]}</text>
        </g>)}
        {stage === 0 && <text className="method-svg-footer" x="225" y="265" textAnchor="middle">Find the break before adding a tool.</text>}
        {stage === 1 && <g className="method-designed"><path d="M45 186v10h355v-10"/><text x="225" y="232" textAnchor="middle">One agreed way forward.</text></g>}
      </svg>
      <p className="method-flow-note">{flowNotes[stage]}</p>
    </div>
  );
};
const Approach = () => {
  const [active, setActive] = useState(0);
  const rows = useRef([]);
  const manualUntil = useRef(0);
  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    let frame;
    const update = () => {
      frame = undefined;
      if (reduced.matches || window.innerWidth <= 768 || performance.now() < manualUntil.current) return;
      const target = window.innerHeight * .52;
      const positions = rows.current.map(el => el.getBoundingClientRect());
      if (positions[0].top > window.innerHeight || positions[3].bottom < 120) return;
      let nearest = 0;
      positions.forEach((rect, i) => {
        if (Math.abs(rect.top + rect.height / 2 - target) < Math.abs(positions[nearest].top + positions[nearest].height / 2 - target)) nearest = i;
      });
      setActive(nearest);
    };
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(update); };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); if (frame) cancelAnimationFrame(frame); };
  }, []);
  return (
    <section className="approach section" id="approach" aria-labelledby="approach-title">
      <div className="container">
        <div className="approach-heading"><span className="label">Our working method</span><h2 id="approach-title">Change the way you work.<br /><em>Then choose the tools.</em></h2></div>
        <div className="approach-layout">
          <div className="approach-focus">
            <span className="approach-focus-number">0{active + 1}<small> / 04</small></span>
            <p className="approach-output" key={active}>{steps[active].output}</p>
            <MethodFlow stage={active} />
            <div className="approach-progress">{steps.map((step, i) => <span key={step.title} className={i <= active ? 'complete' : ''} />)}</div>
            <span className="approach-hint">Scroll through, or select a stage</span>
          </div>
          <ol className="approach-steps">
            {steps.map((step, index) => (
              <li key={step.title} ref={el => rows.current[index] = el} className={active === index ? 'active' : ''}>
                <button type="button" className="approach-step" aria-pressed={active === index} onClick={() => { manualUntil.current = performance.now() + 1200; setActive(index); }}>
                  <span className="approach-number">0{index + 1}</span>
                  <span className="approach-copy"><span className="approach-step-title">{step.title}</span><span>{step.description}</span><span className="approach-step-detail">{step.detail}</span></span>
                </button>
                {active === index && <div className="method-mobile" aria-live="polite">
                  <p className="method-example">In practice · {step.output}</p>
                  <ol className="method-mobile-flow" aria-label="Illustrative enquiry flow">
                    {(index === 0 ? ['Enquiry', 'Owner unclear', 'Response delayed', 'Outcome unknown'] : ['Enquiry', 'Named owner', 'Agreed follow-up', index === 3 ? 'Reviewed outcome' : 'Visible outcome']).map((label, i) => <li key={label}><span aria-hidden="true">0{i + 1}</span>{label}</li>)}
                  </ol>
                  <p className="method-flow-note">{flowNotes[index]}</p>
                </div>}
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};
export default Approach;
